import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useHotel } from '../context/HotelContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Register = () => {
  const { register, setPassword, login } = useAuth();
  const { hotel } = useHotel();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  // Step 1 — registration info
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  // Step 2 — OTP + password
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  // Step 1 — submit registration
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const otpFromServer = await register(form);
      setRegisteredEmail(form.email);
      // If SMTP is configured, otpFromServer is blank and the OTP comes via email.
      // If SMTP is NOT configured, the server returns the OTP directly so the user can proceed.
      if (otpFromServer) setOtp(String(otpFromServer));
      setStep(2);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — submit OTP + new password
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await setPassword(registeredEmail, otp.trim(), newPassword);
      // Auto-login after setting password
      await login(registeredEmail, newPassword);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <Card className="w-full max-w-md p-8 shadow-xl">

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2].map(s => (
            <React.Fragment key={s}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                s === step ? 'bg-blue-600 text-white' : s < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}>{s < step ? '✓' : s}</div>
              {s < 2 && <div className={`h-0.5 w-8 ${step > 1 ? 'bg-green-500' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {step === 1 ? 'Create Account' : 'Verify & Set Password'}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            {step === 1
              ? `at ${hotel?.hotelName || 'the hotel'}`
              : `Enter the OTP sent to ${registeredEmail}`}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* ── Step 1: Registration Form ── */}
        {step === 1 && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text" value={form.firstName} onChange={set('firstName')} required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text" value={form.lastName} onChange={set('lastName')} required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Doe"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email" value={form.email} onChange={set('email')} required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel" value={form.phone} onChange={set('phone')} required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="+880..."
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full py-3 mt-2">
              {loading ? 'Sending OTP...' : 'Send OTP & Continue'}
            </Button>
          </form>
        )}

        {/* ── Step 2: OTP + Password ── */}
        {step === 2 && (
          <form onSubmit={handleSetPassword} className="space-y-4">

            {/* Dev-mode OTP banner — visible when email is not configured */}
            {otp && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-center">
                <p className="text-xs text-amber-700 font-medium mb-1">
                  📧 Email not configured — your OTP is:
                </p>
                <p className="text-2xl font-mono font-bold text-amber-800 tracking-[0.3em]">{otp}</p>
                <p className="text-xs text-amber-600 mt-1">It has been pre-filled below. Remove this banner once SMTP is set up.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">OTP Code</label>
              <input
                type="text" value={otp} onChange={e => setOtp(e.target.value)} required
                maxLength={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none tracking-widest text-center text-lg font-mono"
                placeholder="Enter OTP"
              />
              <p className="text-xs text-gray-500 mt-1 text-center">
                OTP expires in 15 minutes.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Min. 6 characters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Repeat password"
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full py-3 mt-2">
              {loading ? 'Verifying...' : 'Verify & Create Account'}
            </Button>

            <button
              type="button"
              onClick={() => { setStep(1); setError(''); setOtp(''); setNewPassword(''); setConfirmPassword(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Back to registration
            </button>
          </form>
        )}

        <p className="text-center text-sm text-gray-600 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-600 hover:underline font-medium">Sign in</Link>
        </p>
      </Card>
    </div>
  );
};

export default Register;
